import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  AssignTestRequest,
  CreateTestRequest,
  CreateTestResponse,
  GeneratedTest,
  GeneratedTestDetail,
  GeneratedTestSummary,
  RemedialResponse,
  StartAssignmentResponse,
  SubmitAssignmentRequest,
  TestAssignment,
} from "@/lib/api-types";

/**
 * AI test engine hooks (Phase 3, docs/PARENT_PORTAL_PLAN.md §8). Every
 * query/mutation mirrors a route in backend/functions/staxed_tests_handler.
 */

// ---------------------------------------------------------------------------
// Parent side
// ---------------------------------------------------------------------------

/** GET /tests/mine -> a parent's own generated tests (list-shaped, no question bodies). */
export function useMyTests(enabled = true) {
  return useQuery({
    queryKey: qk.tests.mine(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<GeneratedTestSummary[]>>("/tests/mine");
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** GET /tests/{test_id} -> poll while GENERATING, stop once READY/FAILED. */
export function useTest(testId: string | undefined) {
  return useQuery({
    queryKey: qk.tests.detail(testId ?? ""),
    enabled: !!testId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<GeneratedTestDetail>>(`/tests/${encodeURIComponent(testId!)}`);
      return unwrap(res.data);
    },
    refetchInterval: (query) => (query.state.data?.status === "GENERATING" ? 3000 : false),
  });
}

/** POST /tests -> create + async-generate a test. Callable by a PARENT (for a
 * linked child, student_user_id required) or a STUDENT (self-serve, Phase 7 --
 * student_user_id omitted, response includes an auto-created assignment_id). */
export function useCreateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateTestRequest) => {
      const res = await api.post<ApiEnvelope<CreateTestResponse>>("/tests", vars);
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.tests.mine() });
      qc.invalidateQueries({ queryKey: qk.assignments.mine() });
    },
  });
}

/** POST /tests/{test_id}/retry -> retry a FAILED generation, no re-charge. */
export function useRetryTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (testId: string) => {
      const res = await api.post<ApiEnvelope<GeneratedTest>>(`/tests/${encodeURIComponent(testId)}/retry`);
      return unwrap(res.data);
    },
    onSuccess: (data) => {
      if (data) qc.invalidateQueries({ queryKey: qk.tests.detail(data.test_id) });
      qc.invalidateQueries({ queryKey: qk.tests.mine() });
    },
  });
}

/** POST /tests/{test_id}/assign */
export function useAssignTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { testId: string } & AssignTestRequest) => {
      const { testId, ...body } = vars;
      const res = await api.post<ApiEnvelope<TestAssignment>>(`/tests/${encodeURIComponent(testId)}/assign`, body);
      return unwrap(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.tests.detail(vars.testId) });
      qc.invalidateQueries({ queryKey: qk.tests.mine() });
    },
  });
}

/** POST /assignments/{id}/remedial */
export function useRemedialTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const res = await api.post<ApiEnvelope<RemedialResponse>>(`/assignments/${encodeURIComponent(assignmentId)}/remedial`);
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.tests.mine() });
    },
  });
}

// ---------------------------------------------------------------------------
// Student side
// ---------------------------------------------------------------------------

/** GET /assignments/me -> the student's assignment inbox. Polls while any
 * assignment's underlying test is still GENERATING (self-serve, Phase 7). */
export function useMyAssignments(enabled = true) {
  return useQuery({
    queryKey: qk.assignments.mine(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<TestAssignment[]>>("/assignments/me");
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((a) => a.test_status === "GENERATING") ? 3000 : false,
  });
}

/** POST /assignments/{id}/start -> sets server_deadline; returns the answer-stripped test. */
export function useStartAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const res = await api.post<ApiEnvelope<StartAssignmentResponse>>(`/assignments/${encodeURIComponent(assignmentId)}/start`);
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.assignments.mine() });
    },
  });
}

/** POST /assignments/{id}/submit -> scores server-side, returns the completed assignment. */
export function useSubmitAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { assignmentId: string } & SubmitAssignmentRequest) => {
      const { assignmentId, ...body } = vars;
      const res = await api.post<ApiEnvelope<TestAssignment>>(
        `/assignments/${encodeURIComponent(assignmentId)}/submit`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.assignments.mine() });
    },
  });
}
